import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BatchReleaseMode,
  BatchReleasePreviewResponse,
  BatchReleaseResponse,
  BatchReleaseSelectedResponse,
} from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { useSubmitGuard } from '@ooh/ui/lib/use-submit-guard';
import type { OutcomeResultUnitLookup } from '../../reservations/components/outcome-result-buckets';
import { ReleaseSelectedResultView } from '../../reservations/components/unified-select-release-result-view';
import { useBatchRelease, useBatchReleasePreview } from '../../reservations/api/batch-mutations';
import type { ResolvedSelection } from './popover-types';
import type { ReleaseConfirmDialogProps } from './release-confirm-dialog';
import {
  hasUnderCoveredHold,
  isSelectedReleaseResponse,
  normalizeBatchReleaseResponse,
  normalizeBatchReleaseSelectedResponse,
  toBatchCells,
} from './selection-helpers';
import type { UnifiedSelectCellResolution } from '../lib/unified-select-types';

type ReleasableCells = ResolvedSelection['releasableCells'];

interface UseUnifiedSelectReleaseParams {
  releasableCells: ReleasableCells;
  heldByOtherCount: number;
  allResolvedCells: UnifiedSelectCellResolution[];
  canRelease: boolean;
  batchRelease: ReturnType<typeof useBatchRelease>;
  resolveMediaUnitLabel: ((mediaUnitId: number) => string | undefined) | undefined;
  onBatchReleaseSuccess: ((response: BatchReleaseResponse) => void) | undefined;
}

function buildUnitLookup(
  mediaUnitIds: Iterable<number>,
  resolveMediaUnitLabel: ((mediaUnitId: number) => string | undefined) | undefined,
): OutcomeResultUnitLookup {
  const lookup: OutcomeResultUnitLookup = new Map();
  for (const mediaUnitId of mediaUnitIds) {
    if (lookup.has(mediaUnitId)) continue;
    const label = resolveMediaUnitLabel?.(mediaUnitId);
    if (label != null) lookup.set(mediaUnitId, { grwNumber: label, address: null, city: null });
  }
  return lookup;
}

// A unit without a resolvable label stays out of the lookup so result rows fall back to the
// generic unit label instead of showing a null number.
function buildBreakdownLookup(
  items: BatchReleasePreviewResponse,
  resolveMediaUnitLabel: ((mediaUnitId: number) => string | undefined) | undefined,
): OutcomeResultUnitLookup {
  return buildUnitLookup(
    items.map((item) => item.mediaUnitId),
    resolveMediaUnitLabel,
  );
}

function resolveSingleReleaseCell(
  releasableCells: ReleasableCells,
  heldByOtherCount: number,
  anyUnderCovered: boolean,
): ReleasableCells[number] | undefined {
  return releasableCells.length === 1 && heldByOtherCount === 0 && !anyUnderCovered
    ? releasableCells[0]
    : undefined;
}

/**
 * Zwolnij flow of the unified SELECT popover: the confirmation dialog, its pre-commit breakdown,
 * the whole-versus-selected-period choice for an under-covered hold, and the mode='selected'
 * result panel. A mode='full' release gets only a toast with exact counts.
 */
export function useUnifiedSelectRelease({
  releasableCells,
  heldByOtherCount,
  allResolvedCells,
  canRelease,
  batchRelease,
  resolveMediaUnitLabel,
  onBatchReleaseSuccess,
}: UseUnifiedSelectReleaseParams) {
  const { t } = useTranslation('inventory');
  const releasePreview = useBatchReleasePreview();
  const submitGuard = useSubmitGuard();
  const [confirming, setConfirming] = React.useState(false);
  // `null` until the caller picks one; stays null (and `mode` is sent as 'full') when no choice
  // is shown.
  const [releaseMode, setReleaseMode] = React.useState<BatchReleaseMode | null>(null);
  const [releaseResult, setReleaseResult] = React.useState<BatchReleaseSelectedResponse | null>(
    null,
  );

  const anyUnderCovered = React.useMemo(
    () => hasUnderCoveredHold(releasableCells),
    [releasableCells],
  );
  const breakdownLookup = React.useMemo(
    () => buildBreakdownLookup(releasePreview.data ?? [], resolveMediaUnitLabel),
    [releasePreview.data, resolveMediaUnitLabel],
  );
  // Built from `releasableCells` rather than the preview so a hold the breakdown never named
  // still resolves a label in the commit result.
  const resultUnitLookup = React.useMemo(
    () =>
      buildUnitLookup(
        releasableCells.map((cell) => cell.mediaUnitId),
        resolveMediaUnitLabel,
      ),
    [releasableCells, resolveMediaUnitLabel],
  );
  // Result rows name a `reservationId`, not a `mediaUnitId`.
  const reservationUnitLookup = React.useMemo(
    () => new Map(releasableCells.map((cell) => [cell.ownReservation.id, cell.mediaUnitId])),
    [releasableCells],
  );

  function triggerPreview(mode: BatchReleaseMode | null) {
    releasePreview.mutate({ cells: toBatchCells(allResolvedCells), mode: mode ?? 'full' });
  }

  function close() {
    setConfirming(false);
    setReleaseMode(null);
  }

  function handleReleaseClick() {
    if (!canRelease) return;
    // Always confirm, even for a single cell: releasing changes availability, so the caller sees
    // what is about to be released and can cancel.
    setConfirming(true);
    setReleaseResult(null);
    setReleaseMode(null);
    // An under-covered selection shows the mode choice first and fires no breakdown request
    // until a mode is picked.
    if (!anyUnderCovered) triggerPreview(null);
  }

  function handleModeChange(mode: BatchReleaseMode) {
    setReleaseMode(mode);
    triggerPreview(mode);
  }

  function handleSuccess(data: BatchReleaseResponse) {
    close();
    if (isSelectedReleaseResponse(data)) {
      setReleaseResult(normalizeBatchReleaseSelectedResponse(data));
      onBatchReleaseSuccess?.(data);
      return;
    }
    const normalized = normalizeBatchReleaseResponse(data);
    onBatchReleaseSuccess?.(normalized);
    toast.success(
      t('availability.select.releaseResultToast', {
        released: normalized.released.length,
        queuesLeft: normalized.queuesLeft,
      }),
    );
  }

  function submitRelease() {
    // Synchronous same-click guard: `isPending` only flips after a re-render, so two clicks in
    // one tick could both pass the button's `disabled` check.
    if (!submitGuard.tryStart()) return;
    batchRelease.mutate(
      {
        reservationIds: releasableCells.map((cell) => cell.ownReservation.id),
        cells: toBatchCells(allResolvedCells),
        mode: releaseMode ?? 'full',
      },
      { onSuccess: handleSuccess, onSettled: () => submitGuard.finish() },
    );
  }

  const preview = {
    isLoading: releasePreview.isPending,
    isError: releasePreview.isError,
    data: releasePreview.data,
    refetch: () => triggerPreview(releaseMode),
  };
  const choiceMissing = anyUnderCovered && releaseMode === null;

  const dialogProps: ReleaseConfirmDialogProps | null = confirming
    ? {
        singleReleaseCell: resolveSingleReleaseCell(
          releasableCells,
          heldByOtherCount,
          anyUnderCovered,
        ),
        preview,
        lookup: breakdownLookup,
        resolveMediaUnitLabel,
        modeChoice: {
          anyUnderCovered,
          mode: releaseMode,
          disabled: releasePreview.isPending || batchRelease.isPending,
          onChange: handleModeChange,
        },
        submitDisabled: !canRelease || preview.isLoading || preview.isError || choiceMissing,
        onSubmit: submitRelease,
        onClose: close,
      }
    : null;

  const resultNode = releaseResult ? (
    <ReleaseSelectedResultView
      response={releaseResult}
      reservationUnitLookup={reservationUnitLookup}
      unitLookup={resultUnitLookup}
      onDismiss={() => setReleaseResult(null)}
    />
  ) : undefined;

  return { confirming, handleReleaseClick, dialogProps, resultNode };
}
